import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import CommentThread from '../components/CommentThread.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import ReportDialog from '../components/ReportDialog.jsx'
import ReviewCard from '../components/ReviewCard.jsx'
import { ApiError, api, is404 } from '../lib/api.js'

/**
 * What this page is, in one sentence. The hidden `<h1>` renders it and the tab
 * title is it plus ` — Sonologue`, so the page cannot end up with two names
 * (DEC-2026-08-27-001; it read `<name> on Sonologue` until 2026-08-27).
 */
function names(review) {
  return review.media
    ? `Review of ${review.media.title} by ${review.author.display_name}`
    : `Review by ${review.author.display_name}`
}

/**
 * `/reviews/:id` — one review and its thread.
 *
 * A review is a thing people link to, so it has a page of its own rather than
 * living only as a row on an album. The feed card links here; so does a
 * comment notification.
 */
export default function ReviewDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [review, setReview] = useState(null)
  // The error itself, not a sentence about it — the render needs the status.
  const [error, setError] = useState(null)
  // `asking` is the reason dialog open (Q28); `reporting` its send out.
  const [asking, setAsking] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [reported, setReported] = useState(false)
  // One sentence for the three card actions, because they share one slot
  // under the card. Each handler clears it before trying again.
  const [actionError, setActionError] = useState('')

  const load = useCallback(
    async (signal) => {
      setError(null)
      try {
        setReview(await api.review(id, { signal }))
      } catch (err) {
        if (err.name === 'AbortError') return
        setError(err)
      }
    },
    [id],
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    if (review?.author) document.title = `${names(review)} — Sonologue`
    else if (is404(error)) document.title = 'Not found — Sonologue'
  }, [review, error])

  async function react() {
    setActionError('')
    try {
      const result = await api.react('review', id)
      setReview((current) => ({
        ...current,
        reacted: result.reacted,
        reaction_count: result.count,
      }))
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    }
  }

  async function remove() {
    setActionError('')
    try {
      await api.deleteReview(id)
      navigate(-1)
    } catch (err) {
      // No `navigate(-1)`: the review is still here, so the page stays here.
      setActionError(err instanceof ApiError ? err.message : 'That did not delete.')
    }
  }

  async function report(reason) {
    setReporting(true)
    setActionError('')
    try {
      await api.report({ target_type: 'review', target_public_id: id, reason })
      setReported(true)
    } catch (err) {
      // `reported` stays false, so *Report* is still offered and the
      // "Reported." line never claims something that did not happen.
      setActionError(err instanceof ApiError ? err.message : 'That report did not go through.')
    } finally {
      setReporting(false)
      setAsking(false)
    }
  }

  if (error) {
    return is404(error) ? (
      <div className="card card-pad">
        <div className="empty-state">
          <h1>That review is not here.</h1>
          <Link className="btn btn-tonal" to="/">
            Back to your feed
          </Link>
        </div>
      </div>
    ) : (
      <ErrorState title="The review did not load" onRetry={() => load()} level={1} />
    )
  }
  if (!review) return <LoadingState />

  return (
    <>
      {/*
        The card is the page, so the outline gets a heading without the page
        getting a visible one nobody asked for. It lives here rather than in
        `ReviewCard`, which also renders under an album page's own <h1>.
      */}
      <h1 className="sr-only">{names(review)}</h1>
      <ReviewCard
        review={review}
        showMedia
        onReact={react}
        onDelete={review.is_mine ? remove : undefined}
        onReport={review.is_mine || reported ? undefined : () => setAsking(true)}
      />
      <InlineAlert>{actionError}</InlineAlert>
      {reported ? (
        <p className="footnote" role="status">
          Reported. The operator will look at it.
        </p>
      ) : null}
      {reporting ? <p className="footnote">Reporting…</p> : null}
      <ReportDialog
        open={asking}
        busy={reporting}
        onSend={report}
        onClose={() => setAsking(false)}
      />

      <div className="card card-pad">
        <h2 className="title-3">Comments</h2>
        <CommentThread
          kind="reviews"
          publicId={id}
          comments={review.comments}
          onChange={() => load()}
          canPost={review.status === 'published'}
        />
      </div>
    </>
  )
}
